import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../api";
import { SEO } from "../components/SEO";
import { resolveMediaUrl } from "../components/PageContent";

export default function StoryDetail() {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/stories/${slug}`)
      .then((r) => setStory(r.data))
      .catch(() => setError("Story has wandered off."));
  }, [slug]);

  if (error)
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="story-not-found">
        <SEO title="Story not found" path={`/stories/${slug}`} noindex />
        <div className="font-western text-3xl mb-4">{error}</div>
        <Link className="font-mono text-sm underline" to="/stories">Back to stories</Link>
      </div>
    );
  if (!story) return <div className="py-24 text-center font-mono text-xs uppercase tracking-[0.3em]">Loading the file…</div>;

  return (
    <article className="bg-bone" data-testid="story-detail-page">
      <SEO
        title={story.title}
        description={story.excerpt}
        image={story.cover_image}
        type="article"
        path={`/stories/${story.slug}`}
      />
      <header className="bg-asphalt text-bone">
        <div className="max-w-3xl mx-auto px-5 lg:px-0 py-16">
          <Link to="/stories" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] mb-8 hover:text-terra" data-testid="back-to-stories">
            <ArrowLeft className="w-4 h-4" /> Back to stories
          </Link>
          <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-4">✦ Field Report</div>
          <h1 className="font-display text-5xl sm:text-6xl uppercase leading-[0.95] tracking-tighter">{story.title}</h1>
          {story.subtitle && <p className="font-western text-2xl text-bone/80 mt-4">{story.subtitle}</p>}
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-bone/70 mt-6">
            By {story.author || "The Crew"} · {new Date(story.created_at).toLocaleDateString()}
          </div>
        </div>
      </header>
      {story.cover_image && (
        <div className="border-y-4 border-asphalt overflow-hidden">
          <img src={resolveMediaUrl(story.cover_image)} alt={story.title} className="w-full max-h-[600px] object-cover ink-stamp" />
        </div>
      )}
      <div className="max-w-3xl mx-auto px-5 lg:px-0 py-16">
        <div className="font-body text-lg leading-relaxed whitespace-pre-line text-ink">{story.body}</div>
        <div className="border-t-2 border-asphalt mt-12 pt-6 flex flex-wrap gap-2">
          {(story.tags || []).map((t) => (
            <span key={t} className="font-mono text-[10px] uppercase tracking-[0.3em] border-2 border-asphalt px-2 py-1">
              ✦ {t}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}
